import { CallRecordingPlayer } from '@/components/CallRecordingPlayer';
import { CommunicationDirectionCell } from '@/components/CommunicationDirectionCell';
import { CommunicationLogActions } from '@/components/CommunicationLogActions';
import { EmptyCell } from '@/components/EmptyCell';
import { SendSmsModal, SmsTarget } from '@/components/SendSmsModal';
import { TeammateAvatar } from '@/components/TeamAvatar/TeammateAvatar';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { usePartnerAccess } from '@/hooks/usePartnerAccess';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import { getBariatricProcedureStageLabel } from '@/services/patient-fields';
import { getRowNavigationUrl } from '@/services/log-row-navigation';
import { ProviderDirectory } from '@/services/provider-directory';
import {
  describeIvrTemplate,
  describeProviderReference,
  formatCallLength,
  getCallRoutingPath,
  getCallTeammate,
  getIvrSelection,
} from '@bariendo/lib/call-log';
import {
  getCommunicationContactName,
  getCommunicationContactReference,
  getExternalNumberFromPhoneCommunication,
} from '@bariendo/lib/communication';
import { CommunicationCategory, getProviderRoleLabel } from '@bariendo/lib/constants';
import {
  deriveVoiceCallDisplayStatus,
  getCallDurationSeconds,
  getVoiceRecordingPayloadAttachments,
  VOICE_CALL_DISPLAY_BADGE,
} from '@bariendo/lib/voice-call-outcome';
import { Badge, Box, Container, Group, Modal, Paper, Stack, Text, Title, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { formatDateTime, formatHumanName, getReferenceString, isResource, resolveId } from '@medplum/core';
import { Communication, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

/**
 * The log fetches what it renders rather than what the resource happens to carry.
 *
 * Three of these are load-bearing and easy to drop by accident:
 * - `identifier` is the answer evidence. A second Twilio call SID is merged on when a
 *   provider's leg connects, and it is the only signal that separates a call somebody took
 *   from one that merely rang. Without it every inbound call reads as answered.
 * - the **nested** `extension` carries the routing history and the base64 Twilio callbacks
 *   the menu option and the call length are decoded from. A flat `extension { url
 *   valueString }` returns none of it, silently.
 * - `payload.contentAttachment` is the recording. Its URL only; the audio itself is fetched
 *   when somebody presses play.
 *
 * `recipient` resolves only the Organization branch on purpose. `Organization.name` is a
 * `String` and `Practitioner.name` is a `[HumanName!]`, so selecting both branches is a
 * GraphQL type conflict; practitioner names come from the directory instead — and from
 * `display` for the teammates the directory does not hold, which is why both `sender` and
 * `recipient` select it.
 */
export const PHONE_CALLS_QUERY = `
    query {
      CommunicationList(category: "${CommunicationCategory.PHONE_CALL}", _sort: "-sent") {
        resourceType
        id
        sent
        status
        identifier {
          system
          value
        }
        subject {
          reference
          resource {
            ... on Patient {
              resourceType
              id
              name {
                family
                given
              }
              meta {
                tag {
                  system
                  code
                }
              }
              extension {
                url
                valueString
              }
            }
          }
        }
        sender {
          reference
          display
          resource {
            ... on Organization {
              resourceType
              id
              name
            }
          }
        }
        recipient {
          reference
          display
          resource {
            ... on Organization {
              resourceType
              id
              name
            }
          }
        }
        category {
          coding {
            code
            display
          }
        }
        medium {
          coding {
            code
            display
          }
        }
        payload {
          id
          contentAttachment {
            url
            contentType
            title
          }
        }
        extension {
          url
          valueString
          extension {
            url
            valueString
            valueReference {
              reference
              display
            }
            valueAttachment {
              contentType
              data
            }
          }
        }
      }
    }
  `;

interface NamedProvider {
  name: string;
  /** "Care Coordinator", or empty when they hold no role we know about. */
  role: string;
  /** `Practitioner/<id>`. The list key, and the avatar's fallback subject. */
  reference: string;
  /**
   * The resource, when the roster holds them. Passed to the avatar so it renders their
   * photo from what the page already has rather than fetching one per row.
   */
  resource?: Practitioner;
}

/** How many of the path fits in the cell before it becomes a `+N`. */
const ROUTING_PATH_INLINE_LIMIT = 6;

function describeProvider(
  provider: Reference<Practitioner> | undefined,
  resolveProvider: (reference: string) => Practitioner | undefined,
  directory: ProviderDirectory
): NamedProvider | undefined {
  const resource = provider?.reference ? resolveProvider(provider.reference) : undefined;
  const name = describeProviderReference(provider, (reference) => {
    const found = resolveProvider(reference);
    return found ? (formatHumanName(found.name?.[0]) ?? undefined) : undefined;
  });

  if (!name) {
    return undefined;
  }

  const id = provider?.reference ? resolveId(provider) : undefined;
  return {
    name,
    role: getProviderRoleLabel(id ? directory.roleByPractitionerId.get(id) : undefined),
    reference: provider?.reference ?? name,
    resource,
  };
}

/** "Ana Ruiz · Care Coordinator", or just the name when we don't know their role. */
function nameWithRole(provider: NamedProvider): string {
  return provider.role ? `${provider.name} · ${provider.role}` : provider.name;
}

function getDisplayName(communication: Communication): string {
  return getCommunicationContactName(communication) ?? 'Unknown';
}

export function PhoneCallsPage(): ReactElement {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState<Communication | undefined>();
  const [playerOpened, { open: openPlayer, close: closePlayer }] = useDisclosure(false);
  const [texting, setTexting] = useState<SmsTarget | undefined>();
  const [textOpened, { open: openText, close: closeText }] = useDisclosure(false);
  const hasPartnerAccess = usePartnerAccess();

  // One directory read for the whole page. The routing history and `recipient` are bare
  // references, and resolving each one through GraphQL would be a union type conflict on
  // `name` as well as a fetch per row.
  // The session's roster, loaded once by `ProviderDirectoryProvider` for the whole app —
  // roles read the same here as on the timeline, and the page adds no roster read of its
  // own. Holding the resources also means an avatar never has to fetch its own.
  const { directory, practitioners } = useProviderDirectory();

  const practitionersById = useMemo(
    () => new Map(practitioners.map((practitioner) => [practitioner.id!, practitioner])),
    [practitioners]
  );

  const resolveProvider = useCallback(
    (reference: string): Practitioner | undefined => practitionersById.get(resolveId({ reference }) ?? ''),
    [practitionersById]
  );

  const openTextFor = useCallback(
    (call: Communication, phoneNumber: string) => {
      setTexting({
        phoneNumber,
        recipient: getCommunicationContactReference(call),
        displayName: getCommunicationContactName(call),
      });
      openText();
    },
    [openText]
  );

  const playRecordingFor = useCallback(
    (call: Communication) => {
      setPlaying(call);
      openPlayer();
    },
    [openPlayer]
  );

  const { data, loading, scrollPagination } = useLiveGraphQL<Communication>(PHONE_CALLS_QUERY, undefined, {
    enablePagination: true,
    pageSize: 50,
    addNewItemToTop: true,
  });

  const columns = useMemo<MRT_ColumnDef<Communication>[]>(
    () => [
      {
        id: 'type',
        header: '',
        enableSorting: false,
        size: 90,
        enableGlobalFilter: false,
        accessorFn: (row) => row,
        Cell: ({ cell }) => <CommunicationDirectionCell communication={cell.getValue<Communication>()} />,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 120,
        enableGlobalFilter: false,
        accessorFn: (row) => row,
        Cell: ({ cell }) => {
          const call = cell.getValue<Communication>();
          const phoneNumber = getExternalNumberFromPhoneCommunication(call);
          return (
            <CommunicationLogActions
              phoneNumber={phoneNumber}
              recipient={getCommunicationContactReference(call)}
              onText={() => phoneNumber && openTextFor(call, phoneNumber)}
              onPlayRecording={
                getVoiceRecordingPayloadAttachments(call).length > 0 ? () => playRecordingFor(call) : undefined
              }
            />
          );
        },
      },
      {
        id: 'subject',
        header: 'Patient',
        size: 170,
        enableSorting: false,
        accessorFn: (call) => getDisplayName(call),
        Cell: ({ row }) => {
          const name = getDisplayName(row.original);
          return (
            <Tooltip label={name}>
              <Text size="sm" truncate>
                {name}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        id: 'stage',
        header: 'Stage',
        size: 140,
        enableSorting: false,
        accessorFn: (call) => {
          const patient = call.subject?.resource;
          return isResource(patient, 'Patient') ? getBariatricProcedureStageLabel(patient as Patient) : '';
        },
        Cell: ({ cell }) => {
          const label = cell.getValue<string>();
          if (!label) {
            return <EmptyCell />;
          }
          return (
            <Badge size="sm" color="gray" variant="light" radius="xl">
              {label}
            </Badge>
          );
        },
      },
      {
        id: 'number',
        header: 'Number',
        size: 130,
        enableSorting: false,
        accessorFn: (call) => getExternalNumberFromPhoneCommunication(call),
        Cell: ({ cell }) => <Text size="sm">{cell.getValue<string>()}</Text>,
      },
      {
        id: 'sent',
        header: 'Date',
        size: 150,
        enableSorting: true,
        sortingFn: 'datetime',
        accessorFn: (call) => call.sent,
        Cell: ({ cell }) => <Text size="sm">{formatDateTime(cell.getValue<string>())}</Text>,
      },
      {
        id: 'option',
        header: 'Menu',
        size: 100,
        enableSorting: false,
        accessorFn: (call) => {
          const selection = getIvrSelection(call);
          return selection ? `Pressed ${selection.digit}` : '';
        },
        Cell: ({ row }) => {
          const selection = getIvrSelection(row.original);
          if (!selection) {
            return <EmptyCell />;
          }
          return (
            <Tooltip label={describeIvrTemplate(selection.templateId)}>
              <Text size="sm">Pressed {selection.digit}</Text>
            </Tooltip>
          );
        },
      },
      {
        id: 'routedTo',
        header: 'Rang',
        size: 190,
        enableSorting: false,
        // Names, not avatars: the cell shows no text, but a filter still has to find a
        // call by who it rang. Resolved the same way the cell resolves them.
        accessorFn: (call) =>
          getCallRoutingPath(call)
            .map((provider) => describeProvider(provider, resolveProvider, directory)?.name ?? '')
            .join(' '),
        Cell: ({ row }) => {
          const path = getCallRoutingPath(row.original)
            .map((provider) => describeProvider(provider, resolveProvider, directory))
            .filter((provider): provider is NamedProvider => !!provider);

          if (path.length === 0) {
            return <EmptyCell />;
          }

          const shown = path.slice(0, ROUTING_PATH_INLINE_LIMIT);
          const hidden = path.slice(ROUTING_PATH_INLINE_LIMIT);

          // Laid out with a gap rather than as a Mantine `Avatar.Group`: stacked avatars
          // overlap, and this column exists to show how many people a call went through.
          // Ring order is left to right, and each tooltip says its position.
          return (
            <Group gap={4} wrap="nowrap">
              {shown.map((provider, index) => (
                <TeammateAvatar
                  key={provider.reference}
                  practitioner={provider.resource ?? { reference: provider.reference }}
                  size={22}
                  label={`${index + 1}. ${nameWithRole(provider)}`}
                />
              ))}
              {hidden.length > 0 && (
                <Tooltip
                  multiline
                  maw={340}
                  label={hidden
                    .map((provider, index) => `${shown.length + index + 1}. ${nameWithRole(provider)}`)
                    .join('\n')}
                >
                  <Text size="xs" c="dimmed" fw={500}>
                    +{hidden.length}
                  </Text>
                </Tooltip>
              )}
            </Group>
          );
        },
      },
      {
        id: 'teammate',
        header: 'Handled by',
        size: 165,
        enableSorting: false,
        accessorFn: (call) => describeProvider(getCallTeammate(call), resolveProvider, directory)?.name ?? '',
        Cell: ({ row }) => {
          const teammate = describeProvider(getCallTeammate(row.original), resolveProvider, directory);
          if (!teammate) {
            return <EmptyCell />;
          }
          return (
            <Tooltip label={nameWithRole(teammate)}>
              <Group gap={6} wrap="nowrap">
                {/* Decoration beside text that already says who, so no `label` — it would
                    read the name out twice to a screen reader. The ring carries the role. */}
                <TeammateAvatar practitioner={teammate.resource ?? getCallTeammate(row.original)} size={20} />
                <Text size="sm" truncate>
                  {teammate.name}
                </Text>
              </Group>
            </Tooltip>
          );
        },
      },
      {
        id: 'status',
        header: 'Outcome',
        size: 160,
        enableSorting: false,
        accessorFn: (call) => deriveVoiceCallDisplayStatus(call) ?? '',
        Cell: ({ row }) => {
          const call = row.original;
          const status = deriveVoiceCallDisplayStatus(call);
          const badge = status ? VOICE_CALL_DISPLAY_BADGE[status] : undefined;
          const seconds = getCallDurationSeconds(call);

          return (
            <Group gap={6} wrap="nowrap">
              {badge ? (
                <Badge size="sm" variant="light" color={badge.color} radius="sm">
                  {badge.label}
                </Badge>
              ) : (
                <EmptyCell />
              )}
              {/* Zero is what a call still in progress reports, not a call that lasted
                  no time — so it is left off rather than rendered as 0:00. */}
              {seconds > 0 && (
                // The whole inbound leg: menu, ringing and any forward, not time anybody
                // here spent talking. A forwarded row reading "0:47" is 47 seconds of
                // call, not 47 seconds of conversation.
                <Tooltip label="Total call length, including the menu and ringing">
                  <Text size="xs" c="dimmed">
                    {formatCallLength(seconds)}
                  </Text>
                </Tooltip>
              )}
            </Group>
          );
        },
      },
    ],
    [resolveProvider, directory, openTextFor, playRecordingFor]
  );

  const table = useMantineReactTable({
    columns,
    data,
    rowCount: data.length,
    globalFilterFn: 'contains',
    initialState: {
      density: 'xs' as const,
      sorting: [{ id: 'sent', desc: true }],
    },
    state: {
      isLoading: loading,
      showGlobalFilter: false,
    },
    enableRowSelection: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowVirtualization: true,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    mantineTableProps: {
      highlightOnHover: true,
    },
    mantineTableBodyRowProps: ({ row }) => {
      const url = getRowNavigationUrl(row.original, hasPartnerAccess);
      return {
        onClick: () => {
          if (url) {
            navigate(url);
          }
        },
        style: { cursor: url ? 'pointer' : 'default' },
      };
    },
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: scrollPagination?.tableContainerProps,
    mantineTableHeadCellProps: {
      style: {
        fontWeight: '500',
      },
    },
  });

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>Phone logs</Title>
        </Group>

        <Paper style={{ flex: 1, overflow: 'hidden' }}>
          <MantineReactTable table={table} />
        </Paper>
      </Stack>

      {/* The audio is fetched when this opens, not when the page loads: mounting a player
          per row would pull every recording on the page down before anyone asked for one. */}
      <Modal
        opened={playerOpened}
        onClose={closePlayer}
        title={playing ? `${getDisplayName(playing)} · ${formatDateTime(playing.sent)}` : 'Call recording'}
        size="lg"
      >
        {playing && (
          <Box>
            <CallRecordingPlayer
              attachments={getVoiceRecordingPayloadAttachments(playing)}
              resetKey={getReferenceString(playing)}
            />
          </Box>
        )}
      </Modal>

      <SendSmsModal target={texting} opened={textOpened} onClose={closeText} />
    </Container>
  );
}
